import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getCustomer } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Users, Phone, Calendar } from 'lucide-react';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showProfileHistory, setShowProfileHistory] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    last_name: '',
    phone: '',
    email: '',
    size: '',
    modifications: '',
    preferred_color: '',
    birthday: '',
    credit_limit: 0,
    current_debt: 0
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await updateCustomer(editingCustomer.id, formData);
        toast.success('Cliente actualizado');
      } else {
        await createCustomer(formData);
        toast.success('Cliente creado');
      }
      setIsDialogOpen(false);
      resetForm();
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cliente');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      last_name: customer.last_name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      size: customer.size || '',
      modifications: customer.modifications || '',
      preferred_color: customer.preferred_color || '',
      measurements: customer.measurements || {},
      birthday: customer.birthday || '',
      credit_limit: customer.credit_limit || 0,
      current_debt: customer.current_debt || 0,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este cliente?')) {
      try {
        await deleteCustomer(id);
        toast.success('Cliente eliminado');
        loadCustomers();
      } catch (error) {
        toast.error('Error al eliminar cliente');
      }
    }
  };

  const handleViewDetails = async (customerId) => {
    try {
      const customer = await getCustomer(customerId);
      setSelectedCustomer(customer);
      setShowHistory(false);
      setShowProfileHistory(false);
      setIsDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles del cliente');
    }
  };

  const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2', '4', '6', '8', '10', '12', '14', '16'];
  const MEAS_KEYS = ['pecho', 'cintura', 'cadera', 'largo', 'manga'];
  const MEAS_LABELS = { pecho: 'Pecho', cintura: 'Cintura', cadera: 'Cadera', largo: 'Largo', manga: 'Manga' };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      last_name: '',
      phone: '',
      email: '',
      size: '',
      modifications: '',
      preferred_color: '',
      birthday: '',
      credit_limit: 0,
      current_debt: 0
    });
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Clientes</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              data-testid="add-customer-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuevo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Apellido *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="customer-lastname-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    data-testid="customer-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="cliente@ejemplo.com"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Talla</Label>
                <Select
                  value={formData.size || ''}
                  onValueChange={(v) => setFormData({ ...formData, size: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="customer-size-input">
                    <SelectValue placeholder="Selecciona talla" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin talla —</SelectItem>
                    {SIZES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Medidas detalladas */}
              <div className="space-y-2">
                <Label className="font-semibold">Medidas detalladas</Label>
                <div className="grid grid-cols-2 gap-2">
                  {MEAS_KEYS.map((k) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-xs text-slate-500">{MEAS_LABELS[k]}</Label>
                      <Input
                        value={(formData.measurements || {})[k] || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          measurements: { ...(formData.measurements || {}), [k]: e.target.value }
                        })}
                        placeholder='ej. 36"'
                        className="h-8 text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Color Preferido</Label>
                <Input
                  value={formData.preferred_color}
                  onChange={(e) => setFormData({ ...formData, preferred_color: e.target.value })}
                  data-testid="customer-color-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Modificaciones</Label>
                <Input
                  value={formData.modifications}
                  onChange={(e) => setFormData({ ...formData, modifications: e.target.value })}
                  data-testid="customer-modifications-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Cumpleaños</Label>
                <Input
                  type="date"
                  value={formData.birthday}
                  onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                  data-testid="customer-birthday-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Límite de Crédito</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.credit_limit}
                    onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) })}
                    data-testid="customer-credit-limit-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Deuda Actual</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.current_debt}
                    onChange={(e) => setFormData({ ...formData, current_debt: parseFloat(e.target.value) })}
                    data-testid="customer-current-debt-input"
                  />
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1" data-testid="save-customer-button">
                  {editingCustomer ? 'Actualizar' : 'Crear'} Cliente
                </Button>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} data-testid="cancel-customer-button">
                  Cancelar
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-grid">
        {customers.map(customer => (
          <Card
            key={customer.id}
            className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
            onClick={() => handleViewDetails(customer.id)}
            data-testid={`customer-card-${customer.id}`}
          >
            <CardHeader>
              <CardTitle className="flex items-start justify-between" style={{ fontFamily: 'Outfit' }}>
                <div>
                  <h3 className="text-xl">{customer.name} {customer.last_name}</h3>
                  <p className="text-sm text-slate-500 font-normal flex items-center mt-1">
                    <Phone className="w-4 h-4 mr-1" />
                    {customer.phone}
                  </p>
                </div>
                <Users className="w-5 h-5 text-cyan-500" />
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {customer.email && (
                <div className="text-sm">
                  <span className="text-slate-600">Email:</span> <span className="font-medium">{customer.email}</span>
                </div>
              )}
              {customer.size && (
                <div className="text-sm">
                  <span className="text-slate-600">Talla:</span> <span className="font-medium">{customer.size}</span>
                </div>
              )}
              {customer.preferred_color && (
                <div className="text-sm">
                  <span className="text-slate-600">Color:</span> <span className="font-medium">{customer.preferred_color}</span>
                </div>
              )}
              {customer.birthday && (
                <div className="text-sm flex items-center">
                  <Calendar className="w-4 h-4 mr-1 text-slate-500" />
                  <span className="text-slate-600">{customer.birthday}</span>
                </div>
              )}
              {customer.current_debt > 0 && (
                <div className="text-sm bg-rose-50 p-2 rounded">
                  <span className="text-rose-600 font-bold">Deuda: ${customer.current_debt.toFixed(2)}</span>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(customer);
                  }}
                  data-testid={`edit-customer-${customer.id}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  className="text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(customer.id);
                  }}
                  data-testid={`delete-customer-${customer.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Customer Details Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              Detalles del Cliente
            </DialogTitle>
          </DialogHeader>
          {selectedCustomer && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-600">Nombre Completo</p>
                  <p className="font-semibold">{selectedCustomer.name} {selectedCustomer.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Teléfono</p>
                  <p className="font-semibold">{selectedCustomer.phone}</p>
                </div>
                {selectedCustomer.email && (
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold">{selectedCustomer.email}</p>
                  </div>
                )}
                {Object.values(selectedCustomer.measurements || {}).some(v => v) && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-600 mb-1">Medidas</p>
                    <div className="grid grid-cols-3 gap-2">
                      {MEAS_KEYS.filter(k => selectedCustomer.measurements?.[k]).map(k => (
                        <div key={k} className="bg-slate-50 rounded px-2 py-1 text-sm">
                          <span className="text-slate-500 text-xs">{MEAS_LABELS[k]}: </span>
                          <span className="font-medium">{selectedCustomer.measurements[k]}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedCustomer.size && (
                  <div>
                    <p className="text-sm text-slate-600">Talla</p>
                    <p className="font-semibold">{selectedCustomer.size}</p>
                  </div>
                )}
                {selectedCustomer.preferred_color && (
                  <div>
                    <p className="text-sm text-slate-600">Color Preferido</p>
                    <p className="font-semibold">{selectedCustomer.preferred_color}</p>
                  </div>
                )}

                 {selectedCustomer.modifications && (
                  <div>
                    <p className="text-sm text-slate-600">Modificaciones</p>
                    <p className="font-semibold">{selectedCustomer.modifications}</p>
                  </div>
                )}
              </div>

              {/* Historial de cambios de perfil */}
              {(selectedCustomer.profile_history || []).length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowProfileHistory((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={showProfileHistory}
                        onCheckedChange={(v) => setShowProfileHistory(!!v)}
                        onClick={(e) => e.stopPropagation()}
                        className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                      />
                      <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>
                        Historial de Cambios
                      </span>
                      <span className="text-xs text-slate-400">
                        ({selectedCustomer.profile_history.length} registro{selectedCustomer.profile_history.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="text-slate-400 text-xs">{showProfileHistory ? '▲ Ocultar' : '▼ Ver'}</span>
                  </button>

                  {showProfileHistory && (
                    <div className="p-4 space-y-4">
                      {/* Línea de tiempo */}
                      <div className="relative">
                        {selectedCustomer.profile_history.map((entry, idx) => (
                          <div key={idx} className="flex gap-3 pb-4 relative">
                            {/* Línea vertical */}
                            {idx < selectedCustomer.profile_history.length - 1 && (
                              <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-slate-200" />
                            )}
                            {/* Punto */}
                            <div className="w-6 h-6 rounded-full bg-cyan-100 border-2 border-cyan-400 flex items-center justify-center shrink-0 mt-0.5">
                              <div className="w-2 h-2 rounded-full bg-cyan-500" />
                            </div>
                            {/* Contenido */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-medium text-slate-700">
                                  {new Date(entry.date).toLocaleDateString('es-DO', { day:'2-digit', month:'short', year:'numeric' })}
                                </span>
                                {entry.order_code && (
                                  <span className="text-xs bg-blue-100 text-blue-700 rounded px-1.5 py-0.5">
                                    {entry.order_code}
                                  </span>
                                )}
                                {entry.updated_by && (
                                  <span className="text-xs text-slate-400">por {entry.updated_by}</span>
                                )}
                              </div>
                              <div className="space-y-1">
                                {entry.changes.map((ch, ci) => (
                                  <div key={ci} className="flex items-center gap-2 text-xs bg-white border border-slate-100 rounded px-2 py-1">
                                    <span className="font-medium text-slate-600 w-24 shrink-0">{ch.label}:</span>
                                    <span className="text-red-400 line-through">{ch.from || '—'}</span>
                                    <span className="text-slate-400">→</span>
                                    <span className="text-green-600 font-medium">{ch.to}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedCustomer.purchase_history && selectedCustomer.purchase_history.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Toggle header */}
                  <button
                    type="button"
                    onClick={() => setShowHistory((p) => !p)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={showHistory}
                        onCheckedChange={(v) => setShowHistory(!!v)}
                        onClick={(e) => e.stopPropagation()}
                        className="data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                      />
                      <span className="font-semibold text-sm" style={{ fontFamily: 'Outfit' }}>
                        Historial de Compras
                      </span>
                      <span className="text-xs text-slate-400">
                        ({selectedCustomer.purchase_history.length} registro{selectedCustomer.purchase_history.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="text-slate-400 text-xs">{showHistory ? '▲ Ocultar' : '▼ Ver'}</span>
                  </button>

                  {/* History list */}
                  {showHistory && (
                    <div className="divide-y divide-slate-100">
                      {selectedCustomer.purchase_history.map((invoice, index) => (
                        <div key={index} className="flex justify-between items-center px-4 py-3 hover:bg-slate-50">
                          <div>
                            <p className="font-semibold text-sm">{invoice.invoice_number}</p>
                            <p className="text-xs text-slate-500">{invoice.created_at}</p>
                          </div>
                          <p className="font-bold text-cyan-600 text-sm">RD${invoice.total.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;